import { yupResolver } from "@hookform/resolvers/yup";
import * as Sentry from "@sentry/browser";
import { useQueryClient } from "@tanstack/react-query";
import type { Point } from "geojson";
import { useForm } from "react-hook-form";
import type {
    Company,
    EmissionEventUpdateRequest,
    EmissionRecordView,
    Form,
} from "../../apiClient/generated";
import { useEmissionEventsApiClient } from "../../hooks";
import { findClosestSite } from "../../utils/geopatialUtils";
import {
    EMISSIONS_TABLE_PREFIX,
    invalidateKeyPrefix,
    invalidateSiteEmissions,
} from "../../utils/queryInvalidation";
import { useFormDefinition } from "./formDefinition";
import { useFormValidationSchema } from "./formSchema";

export const useEventForm = (
    emissionRecord: EmissionRecordView,
    company: Company | undefined,
    enabledForms: Form[],
    onSaved?: () => void,
) => {
    // Queries to save data
    const queryClient = useQueryClient();
    const eventsApiClient = useEmissionEventsApiClient();

    // Build form schema and definition from separate hooks
    const formSchema = useFormValidationSchema(enabledForms);
    const formDefinition = useFormDefinition(company, enabledForms);

    // Instance form
    const form = useForm({
        defaultValues: {
            similarEmission: null,
            // Event statuses - ALWAYS start out blank
            eventStatus: undefined,
            // Remaining properties
            responsible: emissionRecord.mainEvent.responsible,
            notifyResponsible: true,
            reviewer: emissionRecord.mainEvent.reviewer,
            site: emissionRecord.site?.id
                ? emissionRecord.site.id
                : emissionRecord.mainEvent.eventStatus === "NEW"
                  ? (findClosestSite(
                        emissionRecord.siteMatches,
                        emissionRecord.dataPoint.location as Point,
                    )?.id ?? null)
                  : null,
            equipment: emissionRecord.equipment?.id,
            extraNotes: emissionRecord.mainEvent.extraNotes || "",
            formData: emissionRecord.mainEvent.formData || null,
        },
        resolver: yupResolver(formSchema),
        mode: "onBlur",
        criteriaMode: "all",
    });

    // Handle saving changes
    const saveChanges = async (data) => {
        const dataToSave: EmissionEventUpdateRequest = {
            // `undefined` values are dropped from the JSON body, but the
            // backend requires `site` and `equipment` to be sent together
            // (both are overwritten on the emission record) — send explicit
            // nulls so neither key disappears.
            site: data.site ?? null,
            equipment: data.equipment ?? null,
            eventStatus: data.eventStatus,
            mainEvent: data.similarEmission ?? undefined,
            responsible:
                data.responsible && data.responsible !== null
                    ? data.responsible
                    : undefined,
            notifyResponsible:
                data.responsible && data.responsible !== null
                    ? data.notifyResponsible
                    : undefined,
            reviewer: data.reviewer,
            extraNotes: data.extraNotes,
            formData: data.formData,
        };

        try {
            await eventsApiClient.emissioneventsUpdate({
                id: emissionRecord.mainEvent.id,
                emissionEventUpdateRequest: dataToSave,
            });
        } catch (error) {
            // Network-level failures (offline, CORS, timeout) have no
            // `.response` — fall back to the error itself.
            const errorText = error?.response
                ? await error.response.text().catch(() => String(error))
                : String(error);

            // Parse DRF field errors and surface them inline on the form.
            // Flat API key → form field name (only needed when they differ).
            const API_TO_FORM_FIELD: Record<string, string> = {
                // snake_case → camelCase for nested object fields
                form_data: "formData",
            };
            // Normalize DRF error entries which may be strings or
            // {message, code} objects, and surface every error per field
            // via react-hook-form's `types` map.
            const toMessage = (m: unknown): string => {
                if (typeof m === "string") return m;
                if (m && typeof m === "object" && "message" in m) {
                    return String((m as { message: unknown }).message);
                }
                return String(m);
            };
            const setFieldErrors = (name: string, messages: unknown) => {
                const list = Array.isArray(messages)
                    ? messages.map(toMessage)
                    : [toMessage(messages)];
                if (list.length === 0) return;
                const types: Record<string, string> = {};
                list.forEach((msg, i) => {
                    types[`server_${i}`] = msg;
                });
                form.setError(name as any, {
                    type: "server",
                    message: list[0],
                    types,
                });
            };

            let isDjangoValidationError = false;
            // Only a 400 response carries DRF field validation errors;
            // 403/404/500 JSON bodies (e.g. {"detail": ...}) are unexpected.
            if (error?.response?.status === 400) {
                try {
                    const parsed = JSON.parse(errorText);
                    if (
                        parsed &&
                        typeof parsed === "object" &&
                        !Array.isArray(parsed)
                    ) {
                        isDjangoValidationError = true;
                        const knownFields = new Set(
                            Object.keys(form.getValues()),
                        );
                        // Collected across fields and set once, so multiple
                        // root-level errors don't overwrite each other.
                        const rootMessages: string[] = [];
                        Object.entries(parsed).forEach(
                            ([apiField, messages]) => {
                                const formField =
                                    API_TO_FORM_FIELD[apiField] ?? apiField;

                                // Non-field errors have no input to attach
                                // to — surface them as a root error.
                                if (
                                    apiField === "non_field_errors" ||
                                    apiField === "detail"
                                ) {
                                    const list = Array.isArray(messages)
                                        ? messages
                                        : [messages];
                                    rootMessages.push(...list.map(toMessage));
                                    return;
                                }

                                // Nested object errors e.g.
                                // form_data: {"form_data": {"key": [...]}}
                                if (
                                    messages &&
                                    typeof messages === "object" &&
                                    !Array.isArray(messages)
                                ) {
                                    Object.entries(
                                        messages as Record<string, unknown>,
                                    ).forEach(([subKey, subMessages]) => {
                                        setFieldErrors(
                                            `${formField}.${subKey}`,
                                            subMessages,
                                        );
                                    });
                                    return;
                                }

                                // Unknown API fields would render nowhere —
                                // route to the root error, keeping the field
                                // name in the message.
                                if (!knownFields.has(formField)) {
                                    const list = Array.isArray(messages)
                                        ? messages
                                        : [messages];
                                    rootMessages.push(
                                        ...list.map(
                                            (m) =>
                                                `${apiField}: ${toMessage(m)}`,
                                        ),
                                    );
                                    return;
                                }

                                // Flat field error e.g. responsible / reviewer
                                setFieldErrors(formField, messages);
                            },
                        );
                        if (rootMessages.length > 0) {
                            setFieldErrors("root.server", rootMessages);
                        }
                    }
                } catch {
                    // Non-JSON 400 body -> treat as unexpected below
                }
            }

            // Only report unexpected errors to Sentry
            // DRF validation responses are user-facing errors.
            if (!isDjangoValidationError) {
                Sentry.addBreadcrumb({
                    category: "events",
                    message: errorText,
                    level: "error",
                });
                Sentry.captureException(error);
                form.setError("root.server", {
                    type: "server",
                    message:
                        "Something went wrong while saving. Please try again.",
                });
            }

            // Don't call onSaved on errors and keep the form open.
            return;
        }

        await Promise.all([
            invalidateKeyPrefix(queryClient, EMISSIONS_TABLE_PREFIX),
            invalidateSiteEmissions(queryClient),
            queryClient.invalidateQueries({ queryKey: ["emissionRecord"] }),
            queryClient.invalidateQueries({
                queryKey: ["emissionRecordForEvent"],
            }),
            queryClient.invalidateQueries({
                queryKey: ["emissionsRelatedToEvent"],
            }),
            queryClient.invalidateQueries({ queryKey: ["formsForEvent"] }),
            queryClient.invalidateQueries({ queryKey: ["event"] }),
        ]);
        onSaved?.();
    };

    return {
        form,
        formDefinition,
        submit: form.handleSubmit(saveChanges),
    };
};
